import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { correctlyFormatDate, today } from 'utils/dates';

import CanPerform from 'components/shared/CanPerform';
import { REDEEM_COMPANION_VOUCHERS } from 'constants/afterSalesShortcuts';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import filterLegsByProductItemRefs from 'utils/filterLegsByProductItemRefs';
import { getBookingAttachedLoyaltyAccount, isUserInEBSCOffice } from 'utils/booking';
import { COMFORT, ER_STANDARD, PLUS, SP, STANDARD, STD } from 'constants/classOfService';
import Modal from 'components/ui/Modal';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_BOOKINGS_BY_PRODUCT_CODE, GET_CUSTOMER_DETAILS, GET_BOOKING } from 'store/queries';
import { MAX_NUMBER_OF_COMPANION_VOUCHER } from 'constants/clubEurostar';
import { LOYALTY_COMPANION_PRODUCT_CODES } from 'constants/booking';

import { CtaButton } from '../AfterSalesCtas.styles';
import {
  runAfterSaleValidator,
  allSegmentsOfType,
  allSegmentsHaveSameCurrency,
  bookingIsConfirmed,
  allSelectedProductsConfirmed,
  allSegmentsHaveOnePassengers,
  selectedPassengersOfType,
  allSegmentsOfService,
  hasActiveAOP,
  isMembershipAccountHolderTravelling
} from '../AfterSalesCtas.utils';
import { GET_JOURNEYS } from '../../../../../store/queries';
import { ADD_PASSENGERS, PROVISIONALLY_UPDATE_SEAT, REVERT_CHANGE, UPDATE_EXTRAS } from '../../../../../store/mutations';

import { buildPassengers, findEarliestLeg } from './RedeemCompanionVouchersCta.utils';
import { ErrorButton, ModalContainer } from './RedeemCompanionVouchersCta.styles';
import { productFamilyCodes } from '../../../../../types/productFamilyCodes';
import { types } from '../../../../../types/passengers';
import serviceTypeCodes from '../../../../../constants/serviceTypeCodes';
import objectHash from 'object-hash';
import { isUserInCSCOffice } from '../../../../../utils/user';

const RedeemCompanionVouchersCta = ({
  history: {
    push,
    location: { search, pathname, hash }
  },
  label,
  disabled,
  booking,
  selectedItemRefs,
  user
}) => {
  const { outbound, inbound } = booking;

  const membershipNumber = new URLSearchParams(search).get('membershipNumber');
  const attachedMembershipNumber = getBookingAttachedLoyaltyAccount(booking);
  const [showNotLoggedIn, setShowNotLoggedIn] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [passengerList, setPassengerList] = useState([]);
  const [selectedOutboundFare, setSelectedOutboundFare] = useState(null);
  const [selectedInboundFare, setSelectedInboundFare] = useState(null);

  const [error, setError] = useState(null);
  const [showMissingTrain, setShowMissingTrain] = useState(false);
  const [showMissingTrainErrors, setShowMissingTrainErrors] = useState([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const filteredOutbound = filterLegsByProductItemRefs(outbound, selectedItemRefs);
  const filteredInbound = filterLegsByProductItemRefs(inbound, selectedItemRefs);
  const outboundEarliestLeg = findEarliestLeg(filteredOutbound);
  const inboundEarliestLeg = findEarliestLeg(filteredInbound);

  const [addPassenger, { loading: addPassengerLoading }] = useMutation(ADD_PASSENGERS);

  const [updateBooking, { loading: updateBookingLoading }] = useMutation(UPDATE_EXTRAS);

  const [revertChange, { loading: revertChangeLoading }] = useMutation(REVERT_CHANGE, {
    variables: { bookingReference: booking.reference }
  });

  const [provisionallyUpdateSeats, { loading: updateSeatsLoading }] = useMutation(PROVISIONALLY_UPDATE_SEAT);

  const [getCustomerInfo, { called: customerInfoCalled, loading: customerInfoLoading, data: customerData }] =
    useLazyQuery(GET_CUSTOMER_DETAILS);

  const [getBookingsByProductCode, { loading: companionBookingHistoryLoading }] = useLazyQuery(GET_BOOKINGS_BY_PRODUCT_CODE, {
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      await onGetBookingsByProductCodeCompleted(data);
    }
  });

  const customerInfo = customerData?.getCustomerDetails;

  useEffect(() => {
    if (membershipNumber) {
      getCustomerInfo({
        variables: {
          membershipNumber
        }
      });
    }
  }, [getCustomerInfo, membershipNumber]);

  const isSelectedTicketInThePast =
    moment(outboundEarliestLeg?.timing?.isoDate).isBefore(moment.now()) ||
    moment(inboundEarliestLeg?.timing?.isoDate).isBefore(moment.now());

  const isDisabled =
    disabled ||
    (customerInfo && !(customerInfo.profile?.loyalty.tier.code === 'Tier4' || customerInfo.profile?.loyalty.tier.code === 'Tier5')) ||
    isSelectedTicketInThePast ||
    (!isUserInEBSCOffice(user) && !isUserInCSCOffice(user)) ||
    runAfterSaleValidator(booking, selectedItemRefs)([hasActiveAOP]) ||
    !runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([
      bookingIsConfirmed,
      allSegmentsHaveOnePassengers,
      allSelectedProductsConfirmed,
      allSegmentsOfType([STD, ER_STANDARD, STANDARD, SP, COMFORT, PLUS]),
      allSegmentsHaveSameCurrency,
      allSegmentsOfService([serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED]),
      selectedPassengersOfType([types.ADULT, types.YOUTH, types.SENIOR])
    ]);

  const redirectToPaxDetails = () =>
    push(
      `/exchange/${booking.reference}/pax?currency=${booking.currency}&addPassengers=true&waivePoints=true${
        inboundEarliestLeg && selectedInboundFare ? `&InClass=${selectedInboundFare.classOfService.code}` : ''
      }${outboundEarliestLeg && selectedOutboundFare ? `&OutClass=${selectedOutboundFare.classOfService.code}` : ''}${
        membershipNumber ? `&membershipNumber=${membershipNumber}` : ''
      }`
    );

  const showMissingTrainModal = (isInbound, direction) => {
    setShowMissingTrainErrors((current) => [
      ...current,
      {
        isInbound,
        trainNumber: direction.trainNumber,
        date: moment(direction.timing.date).format('DD MMM YYYY')
      }
    ]);
    setShowMissingTrain(true);
  };

  const getDirection = (direction, searchDirection, isInbound) => {
    if (!direction) {
      return null;
    }

    const journey = searchDirection.journeys?.find((jrn) =>
      jrn.fares?.find(
        (fare) =>
          fare.classOfService?.code === direction.classOfService.code &&
          fare.legs?.find(
            (leg) =>
              leg.trainNumber === direction.trainNumber &&
              leg.destination.uic === direction.destination.uic &&
              leg.origin.uic === direction.origin.uic
          )
      )
    );

    if (!journey) {
      showMissingTrainModal(isInbound, direction);

      return null;
    }

    const fare = journey.fares.find(
      (fr) => fr.classOfService.code === direction.classOfService.code && fr.bookingItems.length > 0 && fr.canBeBooked
    );

    if (!fare) {
      showMissingTrainModal(isInbound, direction);

      return null;
    }

    return {
      ...fare,
      direction: isInbound ? 'inbound' : 'outbound'
    };
  };

  const onGetBookingsByProductCodeCompleted = ({ getBookingsByProductCode }) => {
    const totalNonCancelledRequiredProductsBookingCount = getBookingsByProductCode
      ?.map(({ allBookingItems }) => allBookingItems)
      .filter((bookingItems) =>
        bookingItems.some((item) => LOYALTY_COMPANION_PRODUCT_CODES.includes(item.code) && !item.isCancelled)
      ).length;

    if (totalNonCancelledRequiredProductsBookingCount >= MAX_NUMBER_OF_COMPANION_VOUCHER) {
      setWarningMessage('afterSales.redeemCompanionVoucher.notEnoughVoucher');
      setShowWarningModal(true);
      return;
    }
    if (!isMembershipAccountHolderTravelling({ selectedItemRefs, booking, customerInfo })) {
      setWarningMessage('afterSales.redeemCompanionVoucher.accountHolderMustTravel');
      setShowWarningModal(true);
      return;
    }
    const passengers = buildPassengers({
      booking,
      adults: 1
    });
    setPassengerList(passengers);

    const variables = {
      productFamilies: [productFamilyCodes.LOYALTY_COMPANIOIN],
      currency: booking.currency,
      passengers,
      isYouthAdultConversionActive: false
    };

    if (outboundEarliestLeg) {
      variables.origin = outboundEarliestLeg.origin.uic;
      variables.destination = outboundEarliestLeg.addOnLeg
        ? outboundEarliestLeg.addOnLeg.destination.uic
        : outboundEarliestLeg.destination.uic;
      variables.outboundDate = outboundEarliestLeg.timing.date;
    }

    if (inboundEarliestLeg) {
      variables.inboundDate = inboundEarliestLeg.timing.date;
      variables.returningOrigin = inboundEarliestLeg.addOnLeg ? inboundEarliestLeg.addOnLeg.origin.uic : inboundEarliestLeg.origin.uic;
      variables.returningDestination = inboundEarliestLeg.destination.uic;

      if (!outboundEarliestLeg) {
        variables.origin = inboundEarliestLeg.addOnLeg ? inboundEarliestLeg.addOnLeg.origin.uic : inboundEarliestLeg.origin.uic;
        variables.destination = inboundEarliestLeg.destination.uic;
      }
    }

    journeySearch({
      variables
    });
  };
  const onJourneySearchCompleted = async (journeySearchData) => {
    const newOutboundFare = getDirection(outboundEarliestLeg, journeySearchData.journeySearch.outbound, false);
    const newInboundFare = getDirection(inboundEarliestLeg, journeySearchData.journeySearch.inbound, true);

    if ((outboundEarliestLeg && !newOutboundFare) || (inboundEarliestLeg && !newInboundFare)) {
      return;
    }
    setSelectedOutboundFare(newOutboundFare);
    setSelectedInboundFare(newInboundFare);
    try {
      await addPassenger({
        variables: {
          bookingReference: booking.reference,
          passengers: passengerList.map((p) => ({
            type: p.type,
            disabilityType: p.disabilityType
          }))
        }
      });

      const segments = [];

      if (outboundEarliestLeg) {
        segments.push(buildSegments(newOutboundFare, false));
      }

      if (newInboundFare) {
        segments.push(buildSegments(newInboundFare, true));
      }
      try {
        await updateBooking({
          variables: {
            currency: booking.currency,
            bookingReference: booking.reference,
            segments
          }
        });

        getBooking({
          variables: { reference: booking.reference }
        });
      } catch (err) {
        await revertChange();
        toggleIsOpenModal();
        setError(err);
      }
    } catch (err) {
      toggleIsOpenModal();
      setError(err);
    }
  };

  const onUpdateNewPaxSeatsCloseTo = async (newBooking) => {
    const inputSeats = [];

    newBooking.pendingCreationLegs.forEach((newLeg) => {
      newLeg.products.forEach((newProduct) => {
        const existingLeg = newBooking.outbound
          .concat(newBooking.inbound)
          .find((leg) => leg.products.some((l) => l.itemRef === newProduct.itemRef));

        const existingSeat = existingLeg.seats.find((s) => s.itemRef !== newProduct.itemRef);

        inputSeats.push({
          carriage: existingSeat.coach,
          number: existingSeat.number,
          itemRef: Number(newProduct.itemRef),
          strategy: 'close_to'
        });
      });
    });

    try {
      await provisionallyUpdateSeats({
        variables: {
          reference: booking.reference,
          seats: inputSeats
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const [journeySearch, { loading: journeySearchLoading }] = useLazyQuery(GET_JOURNEYS, {
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      await onJourneySearchCompleted(data);
    }
  });

  const [getBooking, { loading: getBookingLoading }] = useLazyQuery(GET_BOOKING, {
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      await onUpdateNewPaxSeatsCloseTo(data.booking);
      redirectToPaxDetails();
    },
    onError: () => {
      redirectToPaxDetails();
    }
  });
  const isLoading =
    journeySearchLoading ||
    getBookingLoading ||
    addPassengerLoading ||
    companionBookingHistoryLoading ||
    updateSeatsLoading ||
    updateBookingLoading ||
    revertChangeLoading ||
    customerInfoLoading;

  const isWrongAccountLoggedIn = attachedMembershipNumber && membershipNumber && membershipNumber !== attachedMembershipNumber;

  const handleClick = () => {
    if (disabled) {
      return;
    }

    if (!customerInfoCalled || !customerInfo) {
      setShowNotLoggedIn(true);
      return;
    }

    if (isWrongAccountLoggedIn) {
      setShowNotLoggedIn(true);
      return;
    }

    const pointsResetDate = customerInfo.profile?.loyalty.tier.pointsResetDate;
    const startDate = correctlyFormatDate(moment(pointsResetDate).subtract(1, 'year'));
    const endDate = moment(today()).isBefore(moment(pointsResetDate)) ? today() : pointsResetDate;

    const bookingCreationDate = moment(booking.createTimestamp);
    if (bookingCreationDate.isBefore(moment(startDate)) || bookingCreationDate.isAfter(moment(pointsResetDate))) {
      setWarningMessage('afterSales.redeemCompanionVoucher.membershipDateWarning');
      setShowWarningModal(true);
      return;
    }

    if (outboundEarliestLeg) {
      const outboundDate = moment(outboundEarliestLeg.timing.date);
      if (outboundDate.isBefore(moment(startDate)) || outboundDate.isAfter(moment(pointsResetDate))) {
        setWarningMessage('afterSales.redeemCompanionVoucher.membershipDateWarning');
        setShowWarningModal(true);
        return;
      }
    }
    if (inboundEarliestLeg) {
      const inboundDate = moment(inboundEarliestLeg.timing.date);
      if (inboundDate.isBefore(moment(startDate)) || inboundDate.isAfter(moment(pointsResetDate))) {
        setWarningMessage('afterSales.redeemCompanionVoucher.membershipDateWarning');
        setShowWarningModal(true);
        return;
      }
    }
    const getBookingsByProductCodeVariables = {
      startDate,
      endDate,
      passengerName: customerInfo.firstName,
      passengerEmail: customerInfo.emailAddress,
      productCodes: LOYALTY_COMPANION_PRODUCT_CODES,
      customerBookingsOnly: true
    };

    getBookingsByProductCode({ variables: getBookingsByProductCodeVariables });
  };

  const toggleIsOpenModal = () => {
    setSelectedOutboundFare(null);
    setSelectedInboundFare(null);
    setPassengerList([]);
    setIsOpen(!isOpen);
  };

  const toggleWarningModal = () => setShowWarningModal(!showWarningModal);
  const toggleNotLoggedInModal = () => setShowNotLoggedIn(!showNotLoggedIn);
  const toggleMissingTrainModal = () => {
    setShowMissingTrain(!showMissingTrain);
    setShowMissingTrainErrors([]);
  };

  const buildSegments = (fare, isInbound) => {
    const leg = fare.legs[0];

    return {
      direction: isInbound ? 'inbound' : 'outbound',
      origin: leg.origin.uic,
      destination: leg.destination.uic,
      serviceName: leg.serviceName,
      serviceIdentifier: leg.serviceIdentifier,
      startValidityDate: leg.timing.departureDate,
      items: fare.bookingItems.map((bi) => ({
        passengerId: bi.passengerId,
        tariffCode: bi.tariffCode
      }))
    };
  };

  if (error) {
    throw new AgentPortalError(error);
  }

  return (
    <>
      <Modal isOpen={showNotLoggedIn} onClose={toggleNotLoggedInModal}>
        <ModalContainer>
          <ErrorButton
            onClick={() =>
              push(`/clubeurostar/manage`, {
                prevPath: pathname,
                prevSearch: search,
                prevHash: hash
              })
            }
            id="error-page-login-cta"
          >
            <FormattedMessage id={isWrongAccountLoggedIn ? 'upgradePoints.wrongLoggedInAccount' : 'upgradePoints.notLoggedIn'} />
          </ErrorButton>
        </ModalContainer>
      </Modal>
      <Modal
        isOpen={showMissingTrain}
        onClose={toggleMissingTrainModal}
        subtitle={
          <strong>
            <FormattedMessage id={'afterSales.redeemCompanionVoucher.missingTrainWarning'} />
          </strong>
        }
      >
        {showMissingTrainErrors.map((error) => (
          <p key={objectHash(error)}>
            <FormattedMessage
              id={`addPassengers.${error.isInbound ? 'missingTrainInbound' : 'missingTrainOutbound'}`}
              values={{
                trainNumber: error.trainNumber,
                date: error.date
              }}
            />
          </p>
        ))}
      </Modal>
      <Modal isOpen={showWarningModal} onClose={toggleWarningModal}>
        <FormattedMessage id={warningMessage} />
      </Modal>
      <CanPerform afterSalesShortcut={REDEEM_COMPANION_VOUCHERS}>
        {(isEligible) =>
          isEligible && (
            <CtaButton
              type="button"
              data-cy="redeem-companion-vouchers-cta"
              disabled={isDisabled || isLoading}
              label={label}
              loading={isLoading}
              onClick={handleClick}
            >
              {label}
            </CtaButton>
          )
        }
      </CanPerform>
    </>
  );
};

RedeemCompanionVouchersCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  history: historyShape,
  label: PropTypes.element,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string)
};

export default RedeemCompanionVouchersCta;
